import React from 'react'
import { Modal, Button, Spin, Alert, Typography } from 'antd'
import { Stack } from '@sureco/design-system'
import Form, { Templates as AntdTemplates } from '@rjsf/antd'
import validator from '@rjsf/validator-ajv8'
import type { IChangeEvent } from '@rjsf/core'
import type { FieldTemplateProps, ObjectFieldTemplateProps } from '@rjsf/utils'
import type {
  CustomerConfigurationInventoryItem,
  DefinitionManifestEntry,
} from '../hooks/useCustomerConfigurationInventory'
import useUpsertCustomerConfiguration from '../hooks/useUpsertCustomerConfiguration'
import useConfigurationSchema from '../hooks/useConfigurationSchema'
import { ConfigurationSelectWidget } from './ConfigurationSelectWidget'
import {
  buildConfigurationUiSchema,
  getInitialFormData,
  toRjsfSchema,
} from '../utils/configurationFormUtils'

const { Text } = Typography

const configurationFormWidgets = {
  SelectWidget: ConfigurationSelectWidget,
}

function ConfigurationObjectFieldTemplate({
  title,
  description,
  properties,
  fieldPathId,
}: ObjectFieldTemplateProps) {
  const depth = fieldPathId?.path?.length ?? 0
  const isRoot = depth === 0

  if (isRoot) {
    return (
      <Stack space={16}>
        {properties.map((property) => (
          <React.Fragment key={property.name}>
            {property.content}
          </React.Fragment>
        ))}
      </Stack>
    )
  }

  const titleFontSize = depth === 1 ? 15 : 13
  const headerColor = depth === 1 ? '#1F2937' : '#374151'

  return (
    <div
      style={{
        marginTop: 4,
        paddingLeft: 12,
        borderLeft: '2px solid #E5E7EB',
      }}
    >
      <Stack space={8}>
        {title && (
          <Text
            strong
            style={{
              fontSize: titleFontSize,
              color: headerColor,
              textTransform: depth === 1 ? 'uppercase' : 'none',
              letterSpacing: depth === 1 ? 0.3 : 0,
            }}
          >
            {title}
          </Text>
        )}
        {description && (
          <Text style={{ fontSize: 12, color: '#6B7280' }}>{description}</Text>
        )}
        <Stack space={12}>
          {properties.map((property) => (
            <React.Fragment key={property.name}>
              {property.content}
            </React.Fragment>
          ))}
        </Stack>
      </Stack>
    </div>
  )
}

const DefaultAntdFieldTemplate = AntdTemplates?.FieldTemplate

function ConfigurationFieldTemplate(props: FieldTemplateProps) {
  const { schema, children, classNames, style } = props

  if (schema.type === 'object') {
    return (
      <div className={classNames} style={style}>
        {children}
      </div>
    )
  }

  if (DefaultAntdFieldTemplate) {
    return <DefaultAntdFieldTemplate {...props} />
  }

  return (
    <div className={classNames} style={style}>
      {children}
    </div>
  )
}

type EditMode = {
  mode: 'edit'
  item: CustomerConfigurationInventoryItem
  definition?: never
}

type CreateMode = {
  mode: 'create'
  definition: DefinitionManifestEntry
  item?: never
}

type EditConfigurationModalProps = (EditMode | CreateMode) & {
  open: boolean
  customerId: string
  onClose: () => void
  onAfterClose: () => void
}

export default function EditConfigurationModal({
  open,
  customerId,
  onClose,
  onAfterClose,
  ...modeProps
}: EditConfigurationModalProps) {
  const {
    mutate,
    isPending,
    error: mutationError,
    reset: resetMutation,
  } = useUpsertCustomerConfiguration()

  const keyToUse =
    modeProps.mode === 'edit' ? modeProps.item.key : modeProps.definition.key
  const titleToUse =
    modeProps.mode === 'edit'
      ? modeProps.item.title
      : modeProps.definition.title
  const descriptionToUse =
    modeProps.mode === 'edit'
      ? modeProps.item.description
      : modeProps.definition.description
  const submitLabel = modeProps.mode === 'edit' ? 'Save' : 'Create'

  const {
    data: schema,
    isLoading: isSchemaLoading,
    error: schemaError,
  } = useConfigurationSchema(open ? keyToUse : null)

  function handleSubmit(formData: IChangeEvent) {
    mutate(
      {
        customerId,
        key: keyToUse,
        value: formData.formData as Record<string, unknown>,
      },
      {
        onSuccess: () => {
          resetMutation()
          onClose()
        },
      }
    )
  }

  function handleCancel() {
    resetMutation()
    onClose()
  }

  function handleAfterClose() {
    resetMutation()
    onAfterClose()
  }

  const mutationErrorMessage =
    mutationError instanceof Error
      ? mutationError.message
      : mutationError
        ? String(mutationError)
        : null

  const schemaErrorMessage =
    schemaError instanceof Error
      ? schemaError.message
      : schemaError
        ? String(schemaError)
        : null

  function renderContent() {
    if (isSchemaLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin tip="Loading schema..." />
        </div>
      )
    }

    if (schemaErrorMessage) {
      return (
        <Alert
          type="error"
          message="Failed to load schema"
          description={schemaErrorMessage}
          showIcon
        />
      )
    }

    if (!schema) {
      return null
    }

    const formSchema = toRjsfSchema(schema)

    return (
      <Stack space={16}>
        <div
          style={{
            background: '#F5FAFB',
            borderRadius: 8,
            padding: '12px 16px',
            border: '1px solid #E8F3F5',
          }}
        >
          <Stack space={4}>
            {descriptionToUse && descriptionToUse.length > 0 && (
              <Text style={{ fontSize: 13, color: '#5B7083' }}>
                {descriptionToUse}
              </Text>
            )}
            <Text
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                color: '#5B7083',
              }}
            >
              {keyToUse}
            </Text>
          </Stack>
        </div>

        {mutationErrorMessage && (
          <Alert
            type="error"
            message="Failed to save configuration"
            description={mutationErrorMessage}
            showIcon
          />
        )}

        <Form
          schema={formSchema}
          validator={validator}
          formData={getInitialFormData(
            modeProps.mode === 'edit' ? modeProps.item.value : undefined,
            schema
          )}
          onSubmit={handleSubmit}
          widgets={configurationFormWidgets}
          templates={{
            ObjectFieldTemplate: ConfigurationObjectFieldTemplate,
            FieldTemplate: ConfigurationFieldTemplate,
          }}
          uiSchema={buildConfigurationUiSchema()}
          id={`edit-config-form-${keyToUse}`}
        />
      </Stack>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={600}
      title={titleToUse}
      afterClose={handleAfterClose}
      destroyOnClose
      styles={{ body: { overflow: 'visible' } }}
    >
      <Stack space={16}>
        {renderContent()}

        {!isSchemaLoading && !schemaErrorMessage && schema && (
          <Stack space={8}>
            <Button
              type="primary"
              block
              loading={isPending}
              htmlType="submit"
              form={`edit-config-form-${keyToUse}`}
            >
              {submitLabel}
            </Button>
            <Button block onClick={handleCancel} disabled={isPending}>
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
